import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import distinctColors from "distinct-colors";
import moment from "moment/moment";
import { useTheme } from "@mui/material";
import { InsightsResponse, Platform } from "../api/v1/api_pb";
import { timestampDate } from "@bufbuild/protobuf/wkt";

interface ImageUsageProps {
  readonly insights: InsightsResponse;
  readonly dateFormat: string;
  readonly platforms: readonly Platform[];
}

function ImageUsage(props: ImageUsageProps) {
  let theme = useTheme();

  const dataKeys = new Set<string>();

  const data = props.insights.aggregates.map((aggregate) => {
    const imageDistributionCount = aggregate.platformMetrics
      .filter((metrics) => props.platforms.includes(metrics.platform))
      .reduce((accumulator, stats) => {
        for (const [key, value] of Object.entries(
          stats.imageDistributionCount,
        )) {
          accumulator.set(key, (accumulator.get(key) ?? 0) + Number(value));
          dataKeys.add(key);
        }

        return accumulator;
      }, new Map<string, number>());

    return {
      date: moment(timestampDate(aggregate.startAt!)).format(props.dateFormat),
      ...Object.fromEntries(imageDistributionCount),
    };
  });

  // Generate a palette of distinct colors with limited chroma
  // to prevent hard to see colors and bleeding eyes and limited
  // hue to use less warm colors in our cold-colored UI
  //
  // A handy tool for customizing this library: https://medialab.github.io/iwanthue/
  const palette = distinctColors({
    count: dataKeys.size,
    hueMin: 75,
    chromaMin: 25,
    chromaMax: 75,
  });

  const bars = Array.from(dataKeys).map((dataKey) => (
    <Bar
      key={dataKey}
      dataKey={dataKey}
      stackId="image"
      fill={palette.pop()!.hex()}
    />
  ));

  return (
    <ResponsiveContainer width="100%" height={350}>
      <BarChart data={data}>
        <XAxis dataKey="date" />
        <YAxis />
        <CartesianGrid strokeDasharray="3 3" />
        <Tooltip
          cursor={false}
          contentStyle={{ backgroundColor: theme.palette.background.paper }}
          formatter={(value) => `${value} runs`}
        />
        {bars}
      </BarChart>
    </ResponsiveContainer>
  );
}

export default ImageUsage;
